import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FiCheckCircle, FiUpload } from "react-icons/fi";
import Notification from "../../../components/ui/Notification";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import EmptyState from "../../../components/ui/EmptyState";
import {
  getDoctorProfile,
  updateDoctorProfile,
  uploadDoctorSignature,
  uploadDoctorStamp,
  getDoctorImageFullUrl,
} from "../../../api/doctorProfile";
import { useAuth } from "../../../context/AuthContext";
import { CLINIC_MODE_SOLO_DOCTOR } from "../../../utils/clinicMode";
import { isClinicAdminRole } from "../../../utils/dashboardMenu";
import { getClinicUserRoleLabel } from "../../../utils/clinicUserDisplay";

const ALLOWED_IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";

function getInitials(name) {
  if (!name || !name.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function ProfileRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-200 py-3.5">
      <span className="text-sm text-slate-500 shrink-0">{label}</span>
      <div className="min-w-0 text-right text-sm font-semibold text-slate-900">{children}</div>
    </div>
  );
}

function AssetRow({ label, imageUrl, emptyText, busy, onFile }) {
  return (
    <ProfileRow label={label}>
      <div className="flex items-center justify-end gap-3">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-10 max-w-[7.5rem] object-contain" />
        ) : (
          <span className="font-normal text-slate-400">{emptyText}</span>
        )}
        <label className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-sky-600 hover:text-sky-700">
          <FiUpload size={14} aria-hidden />
          {busy ? "Duke ngarkuar…" : imageUrl ? "Ndrysho" : "Ngarko"}
          <input
            type="file"
            accept={ALLOWED_IMAGE_TYPES}
            className="sr-only"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              e.target.value = "";
              if (file) onFile(file);
            }}
          />
        </label>
      </div>
    </ProfileRow>
  );
}

export default function DoctorProfile() {
  const navigate = useNavigate();
  const { role, clinicMode } = useAuth();
  const isDoctor =
    (role && String(role).toLowerCase() === "doctor") ||
    (isClinicAdminRole(role) && clinicMode === CLINIC_MODE_SOLO_DOCTOR);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notif, setNotif] = useState({ visible: false, type: "info", message: "" });
  const [displayName, setDisplayName] = useState("");
  const [signatureFile, setSignatureFile] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!isDoctor) return;
    setLoading(true);
    try {
      const data = await getDoctorProfile();
      setProfile(data);
      setDisplayName(data?.displayName ?? data?.DisplayName ?? "");
    } catch (err) {
      const status = err.response?.status;
      const msg =
        status === 403
          ? "Vetëm mjekët mund të shohin këtë faqe."
          : status === 401
            ? "Duhet të jeni të kyçur."
            : err.response?.data?.message ?? err.response?.data ?? "Dështoi ngarkimi i profilit.";
      setNotif({ visible: true, type: "error", message: msg });
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [isDoctor]);

  useEffect(() => {
    if (!isDoctor) {
      navigate("/dashboard", { replace: true });
      return;
    }
    fetchProfile();
  }, [isDoctor, fetchProfile, navigate]);

  useEffect(() => {
    if (!signatureFile) {
      setSignaturePreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(signatureFile);
    setSignaturePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [signatureFile]);

  useEffect(() => {
    if (!stampFile) {
      setStampPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(stampFile);
    setStampPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [stampFile]);

  const showNotif = (type, message) => setNotif({ visible: true, type, message });

  const savedName = profile?.displayName ?? profile?.DisplayName ?? "";
  const nameDirty = displayName.trim() !== savedName;
  const dirty = nameDirty || Boolean(signatureFile) || Boolean(stampFile);

  const handleSave = async () => {
    if (!dirty || saving) return;
    const next = displayName.trim();
    if (next.length > 200) {
      showNotif("error", "Emri nuk duhet të kalojë 200 karaktere.");
      return;
    }
    setSaving(true);
    try {
      let updated = profile;
      if (nameDirty) updated = await updateDoctorProfile({ displayName: next });
      if (signatureFile) updated = await uploadDoctorSignature(signatureFile);
      if (stampFile) updated = await uploadDoctorStamp(stampFile);
      setProfile(updated);
      setDisplayName(updated?.displayName ?? updated?.DisplayName ?? next);
      setSignatureFile(null);
      setStampFile(null);
      showNotif("success", "Profili u ruajt.");
    } catch (err) {
      const status = err.response?.status;
      showNotif(
        "error",
        status === 400
          ? "Skedari nuk është i vlefshëm. Përdorni JPG, PNG, GIF ose WebP."
          : err.response?.data?.message ?? err.response?.data ?? "Dështoi ruajtja."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isDoctor) return null;

  const email = profile?.email ?? profile?.Email ?? "";
  const fullSigUrl =
    signaturePreview || getDoctorImageFullUrl(profile?.signatureUrl ?? profile?.SignatureUrl);
  const fullStampUrl = stampPreview || getDoctorImageFullUrl(profile?.stampUrl ?? profile?.StampUrl);
  const heroName = displayName.trim() || email || "Mjek";
  const roleLabel = getClinicUserRoleLabel(role);

  return (
    <>
      <Notification
        visible={notif.visible}
        type={notif.type}
        message={notif.message}
        onClose={() => setNotif((p) => ({ ...p, visible: false }))}
      />

      <div className="page-shell max-w-5xl">
        {loading ? (
          <LoadingSpinner className="py-16" label="Duke ngarkuar profilin…" />
        ) : !profile ? (
          <div className="card">
            <EmptyState
              icon={FiCheckCircle}
              title="Profili nuk u ngarkua"
              description="Nuk mund të ngarkohet profili i mjekut. Provoni të rifreskoni faqen."
            />
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white px-5 py-8 sm:px-10">
            <div className="mb-10 text-center">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-amber-300 text-2xl font-semibold text-slate-900">
                {getInitials(heroName)}
              </div>
              <h1 className="mt-4 inline-flex items-center gap-1.5 text-xl font-semibold tracking-tight text-slate-900">
                {heroName}
                <FiCheckCircle className="text-sky-500" size={18} aria-label="I verifikuar" />
              </h1>
              <p className="mt-1 text-sm text-slate-500">{email || "—"}</p>
            </div>

            <div className="grid gap-x-16 gap-y-10 md:grid-cols-2">
              <section>
                <h2 className="mb-1 text-sm font-semibold text-slate-800">Të dhënat personale</h2>
                <ProfileRow label="Emri i shfaqur">
                  <input
                    type="text"
                    value={displayName}
                    maxLength={200}
                    aria-label="Emri i shfaqur"
                    disabled={saving}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="p.sh. Dr. Ana Krasniqi"
                    className="w-full max-w-[16rem] bg-transparent text-right text-sm font-semibold text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400"
                  />
                </ProfileRow>
                <ProfileRow label="Email">
                  <span className="font-medium">{email || "—"}</span>
                </ProfileRow>
                <ProfileRow label="Roli">
                  <span>{roleLabel}</span>
                </ProfileRow>
              </section>

              <section>
                <h2 className="mb-1 text-sm font-semibold text-slate-800">Nënshkrimi dhe vula</h2>
                <AssetRow
                  label="Nënshkrimi"
                  imageUrl={fullSigUrl}
                  emptyText="Mungon"
                  busy={saving}
                  onFile={setSignatureFile}
                />
                <AssetRow
                  label="Vula"
                  imageUrl={fullStampUrl}
                  emptyText="Mungon"
                  busy={saving}
                  onFile={setStampFile}
                />
                <ProfileRow label="Përdorimi">
                  <span className="font-medium text-slate-600">Raportet PDF</span>
                </ProfileRow>
              </section>
            </div>

            {dirty ? (
              <div className="mt-8 flex justify-end">
                <button type="button" onClick={handleSave} disabled={saving} className="btn-primary btn-md">
                  {saving ? "Duke ruajtur…" : "Ruaj"}
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}
